import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Col, Container, Input, Nav, NavItem, NavLink, Row } from "reactstrap";
import { getFloors, getTables, fetchBranches, releaseAllTables, releaseTable } from "../../../utils/apiCalls";
import TableStatusHistoryModal from "./table-status-history";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const STATUS_CONFIG = {
  available: { color: C.green, bg: C.greenLight, gradient: `linear-gradient(135deg, ${C.green}, #38F9D7)`, label: "Available", icon: "✓" },
  occupied: { color: C.coral, bg: C.coralLight, gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, label: "Occupied", icon: "●" },
  reserved: { color: C.orange, bg: C.orangeLight, gradient: `linear-gradient(135deg, ${C.orange}, #FFD200)`, label: "Reserved", icon: "◆" },
};

const TableList = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [floors, setFloors] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [activeFloor, setActiveFloor] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyTableId, setHistoryTableId] = useState(null);
  const [historyTableName, setHistoryTableName] = useState("");

  const fetchBranchList = useCallback(async () => {
    const response = await fetchBranches({}, false);
    let branchList = response?.data || [];
    if (selectedMerchant && selectedMerchant !== "all") {
      branchList = branchList.filter((b) => String(b.MerchantId) === String(selectedMerchant));
    }
    setBranches(branchList);
  }, [selectedMerchant]);

  const fetchFloorList = useCallback(async () => {
    if (!selectedBranch) { setFloors([]); return; }
    const data = await getFloors({ branchId: selectedBranch });
    setFloors(data || []);
    setActiveFloor(0);
  }, [selectedBranch]);

  const fetchTableList = useCallback(async () => {
    if (!selectedBranch) { setTables([]); return; }
    setIsLoading(true);
    const params = { branchId: selectedBranch };
    if (selectedMerchant && selectedMerchant !== "all") params.MerchantId = selectedMerchant;
    const data = await getTables(params);
    setTables(data || []);
    setIsLoading(false);
  }, [selectedBranch, selectedMerchant]);

  useEffect(() => { fetchBranchList(); }, [fetchBranchList]);
  useEffect(() => { fetchFloorList(); }, [fetchFloorList]);
  useEffect(() => { fetchTableList(); }, [fetchTableList]);

  useEffect(() => {
    if (!selectedBranch) return;
    const interval = setInterval(fetchTableList, 30000);
    return () => clearInterval(interval);
  }, [selectedBranch, fetchTableList]);

  const handleReleaseAll = async () => {
    if (!window.confirm("Release ALL occupied/reserved tables for this branch? This cannot be undone.")) return;
    const payload = { MerchantId: Number(selectedMerchant) };
    if (selectedBranch) payload.branchId = Number(selectedBranch);
    const result = await releaseAllTables(payload);
    if (result) { fetchTableList(); fetchFloorList(); }
  };

  const handleReleaseOne = async (table) => {
    if (!window.confirm(`Free table "${table.name}"? It will be marked available.`)) return;
    const result = await releaseTable(table.id, { reason: "manual_release" });
    if (result) fetchTableList();
  };

  const currentFloor = floors[activeFloor];
  const floorTables = useMemo(() => {
    if (!currentFloor) return tables;
    return tables.filter((t) => t.floorId === currentFloor.id);
  }, [tables, currentFloor]);

  const statusCounts = useMemo(() => ({
    available: floorTables.filter((t) => t.status === "available").length,
    occupied: floorTables.filter((t) => t.status === "occupied").length,
    reserved: floorTables.filter((t) => t.status === "reserved").length,
  }), [floorTables]);

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Floor & Tables" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: 250, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">Select Branch</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <div style={{ flex: 1 }} />
          {selectedBranch && (statusCounts.occupied + statusCounts.reserved > 0) && (
            <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, fontSize: 12 }} onClick={handleReleaseAll}>
              Release All Tables
            </Button>
          )}
        </div>

        {!selectedBranch ? (
          <RCard><p style={{ textAlign: "center", color: C.textLight, margin: 0 }}>Select a branch to view tables</p></RCard>
        ) : isLoading ? (
          <Loader />
        ) : (
          <>
            {/* Status Tiles */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg], i) => (
                <Tile key={key} label={cfg.label} value={statusCounts[key]} gradient={cfg.gradient} icon={cfg.icon} delay={i * 80} />
              ))}
            </div>

            {/* Floor Tabs */}
            {floors.length > 0 && (
              <Nav tabs style={{ marginBottom: 16, borderBottom: `2px solid ${C.borderLight}` }}>
                {floors.map((floor, idx) => (
                  <NavItem key={floor.id}>
                    <NavLink
                      className={activeFloor === idx ? "active" : ""}
                      onClick={() => setActiveFloor(idx)}
                      style={{ cursor: "pointer", fontWeight: activeFloor === idx ? 700 : 500, color: activeFloor === idx ? C.coral : C.textMed, fontSize: 13 }}
                    >
                      {floor.name}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            )}

            {/* Table Cards Grid */}
            {floorTables.length === 0 ? (
              <RCard><p style={{ textAlign: "center", color: C.textLight, margin: 0 }}>No tables on this floor</p></RCard>
            ) : (
              <Row>
                {floorTables.sort((a, b) => a.tableNumber - b.tableNumber).map((table, i) => {
                  const cfg = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
                  return (
                    <Col sm="3" key={table.id} className="mb-3">
                      <div className="rpt-lift rpt-fade" style={{
                        background: C.white, borderRadius: 14, padding: 16, textAlign: "center",
                        boxShadow: C.shadow, border: `1px solid ${C.border}`, position: "relative", overflow: "hidden",
                        animationDelay: `${i * 40}ms`,
                      }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: cfg.gradient }} />
                        <div style={{
                          width: 48, height: table.shape === "rectangle" ? 32 : 48,
                          borderRadius: table.shape === "round" ? 24 : 10,
                          background: cfg.bg, border: `2px solid ${cfg.color}`,
                          margin: "8px auto 10px", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <i className="fa fa-cutlery" style={{ color: cfg.color, fontSize: 16 }} />
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 2 }}>{table.name}</div>
                        <div style={{ fontSize: 12, color: C.textLight, marginBottom: 8 }}>Seats: {table.seatingCapacity}</div>
                        <Badge color={cfg.color} bg={cfg.bg}>{cfg.label}</Badge>
                        <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 12 }}>
                          <span
                            style={{ fontSize: 12, color: C.blue, cursor: "pointer", fontWeight: 600 }}
                            onClick={() => { setHistoryTableId(table.id); setHistoryTableName(table.name); setHistoryModal(true); }}
                          >
                            <i className="fa fa-history" /> History
                          </span>
                          {table.status !== "available" && (
                            <span
                              style={{ fontSize: 12, color: C.coral, cursor: "pointer", fontWeight: 600 }}
                              onClick={() => handleReleaseOne(table)}
                            >
                              <i className="fa fa-unlock" /> Free
                            </span>
                          )}
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            )}
          </>
        )}
      </Container>

      <TableStatusHistoryModal
        isOpen={historyModal}
        toggle={() => setHistoryModal(false)}
        tableId={historyTableId}
        tableName={historyTableName}
      />
    </Fragment>
  );
};

export default TableList;
